import { Link } from "react-router-dom";

/**
 * Porté depuis apps/web/app/(shop)/politique-cookies/page.tsx : `next/link` →
 * `Link` de react-router-dom (`href` → `to`), `generateMetadata` abandonné
 * (hors périmètre — cf. lot SEO/prérendu séparé).
 *
 * Page dédiée, distincte de `/confidentialite` (qui en donne un résumé) :
 * un cookie sur deux visiteurs cherche cette page par son nom exact, plutôt
 * que de la trouver dans la politique de confidentialité générale.
 *
 * Contenu vérifié dans le code, pas inventé : ces 4 cookies sont les SEULS
 * posés par le site — tous `httpOnly`, aucun cookie de mesure d'audience ni
 * de publicité, aucune dépendance analytics/marketing dans `package.json`.
 */
export function PolitiqueCookiesPage() {
  return (
    <div className="container-vvs py-8 lg:py-12">
      <h1 className="font-display text-2xl font-bold text-navy sm:text-3xl">Politique de cookies</h1>
      <div className="mt-4 max-w-prose space-y-6 font-body text-sm leading-relaxed text-muted">
        <p>
          Un cookie est un petit fichier déposé par un site dans votre navigateur. VVS en utilise le
          moins possible : uniquement des cookies <strong className="text-ink">strictement nécessaires</strong>{" "}
          au fonctionnement du site (retrouver votre panier, rester connecté à votre compte). Aucun
          cookie de mesure d&apos;audience (Google Analytics ou équivalent) ni de publicité
          (Facebook Pixel ou équivalent) n&apos;est déposé.
        </p>

        <div>
          <h2 className="font-display text-base font-semibold text-ink">Pourquoi aucun bandeau « Accepter / Refuser » ?</h2>
          <p className="mt-1">
            La réglementation impose de demander votre consentement uniquement pour les cookies non
            essentiels (mesure d&apos;audience, publicité). VVS n&apos;en dépose aucun à ce jour :
            tous les cookies listés ci-dessous sont nécessaires au fonctionnement du site et ne
            requièrent donc pas de consentement actif — seulement une information claire, ce que
            cette page fournit. Si cela évoluait (ajout d&apos;un outil de mesure d&apos;audience,
            par exemple), cette page et le bandeau du site seraient mis à jour en conséquence.
          </p>
        </div>

        <div>
          <h2 className="font-display text-base font-semibold text-ink">Cookies déposés par VVS</h2>
          <div className="mt-3 overflow-x-auto">
            <table className="w-full min-w-[560px] border-collapse text-left text-xs">
              <thead>
                <tr className="border-b border-line text-muted">
                  <th scope="col" className="py-2 pr-4 font-medium">Nom</th>
                  <th scope="col" className="py-2 pr-4 font-medium">Finalité</th>
                  <th scope="col" className="py-2 pr-4 font-medium">Type</th>
                  <th scope="col" className="py-2 font-medium">Durée</th>
                </tr>
              </thead>
              <tbody className="text-ink">
                <tr className="border-b border-line">
                  <td className="py-2 pr-4 font-mono">vvs_session_token</td>
                  <td className="py-2 pr-4">
                    Identifie votre panier avant connexion (panier « invité »), pour le retrouver
                    d&apos;une page à l&apos;autre et d&apos;une visite à l&apos;autre.
                  </td>
                  <td className="py-2 pr-4">Nécessaire (httpOnly)</td>
                  <td className="py-2">30 jours</td>
                </tr>
                <tr className="border-b border-line">
                  <td className="py-2 pr-4 font-mono">vvs_access_token</td>
                  <td className="py-2 pr-4">Vous garde connecté à votre compte le temps de votre visite.</td>
                  <td className="py-2 pr-4">Nécessaire (httpOnly)</td>
                  <td className="py-2">1 heure</td>
                </tr>
                <tr className="border-b border-line">
                  <td className="py-2 pr-4 font-mono">vvs_refresh_token</td>
                  <td className="py-2 pr-4">
                    Permet de renouveler votre connexion sans devoir ressaisir un code de vérification à
                    chaque visite.
                  </td>
                  <td className="py-2 pr-4">Nécessaire (httpOnly)</td>
                  <td className="py-2">30 jours</td>
                </tr>
                <tr>
                  <td className="py-2 pr-4 font-mono">vvs_customer</td>
                  <td className="py-2 pr-4">
                    Retient votre identité (nom, téléphone) pour afficher votre espace compte sans
                    interroger le serveur à chaque page.
                  </td>
                  <td className="py-2 pr-4">Nécessaire (httpOnly)</td>
                  <td className="py-2">30 jours</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div>
          <h2 className="font-display text-base font-semibold text-ink">Stockage local du navigateur</h2>
          <p className="mt-1">
            En complément des cookies, votre navigateur peut mémoriser localement (sans qu&apos;aucune
            information ne soit transmise à VVS) le fait que vous avez déjà pris connaissance du
            bandeau d&apos;information affiché en bas de page, pour ne pas vous le montrer à nouveau à
            chaque visite.
          </p>
        </div>

        <div>
          <h2 className="font-display text-base font-semibold text-ink">Comment désactiver les cookies ?</h2>
          <p className="mt-1">
            Vous pouvez configurer votre navigateur pour refuser les cookies, mais certaines
            fonctionnalités du site (panier, connexion à votre compte) ne fonctionneront alors plus
            correctement, ces cookies étant nécessaires à leur bon fonctionnement.
          </p>
        </div>

        <p className="mt-1">
          Pour toute question, voir aussi la{" "}
          <Link to="/confidentialite" className="text-navy underline hover:text-golddark">
            politique de confidentialité
          </Link>{" "}
          ou la page{" "}
          <Link to="/contact" className="text-navy underline hover:text-golddark">
            Contact
          </Link>
          .
        </p>
      </div>
    </div>
  );
}
